/* ==========================================================================
   NELVI — theme.js (vanilla, no dependencies)
   - Drawers (menu / cart) on native <dialog>
   - Real Shopify AJAX Cart API + Section Rendering API (locale-aware routes)
   - Variant selection: price, availability, media, URL, cart payload
   - Gallery slider, thumbnails, zoom dialog
   - Sticky mobile add-to-cart, reveal on scroll
   Everything degrades to native Shopify forms when JavaScript is unavailable.
   ========================================================================== */
(function () {
  'use strict';

  var NELVI = window.NELVI || { routes: {}, strings: {} };
  var routes = NELVI.routes || {};
  var strings = NELVI.strings || {};
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isRTL = document.documentElement.getAttribute('dir') === 'rtl';

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function qsa(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function announce(message) {
    var region = qs('[data-live-region]');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  function cssEscape(value) {
    return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/"/g, '\\"');
  }

  /* ------------------------------------------------------------------------
     Drawers
     ------------------------------------------------------------------------ */
  var Drawer = {
    open: function (dialog, trigger) {
      if (!dialog || dialog.open) return;
      dialog._trigger = trigger || document.activeElement;
      dialog.classList.remove('is-closing');
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      document.body.classList.add('is-locked');
      if (trigger && trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
    },
    close: function (dialog) {
      if (!dialog || !dialog.open) return;
      var finish = function () {
        dialog.classList.remove('is-closing');
        if (typeof dialog.close === 'function') {
          dialog.close();
        } else {
          dialog.removeAttribute('open');
          dialog.dispatchEvent(new Event('close'));
        }
      };
      if (reducedMotion || !dialog.matches('[data-drawer]')) {
        finish();
      } else {
        dialog.classList.add('is-closing');
        window.setTimeout(finish, 280);
      }
    }
  };

  function bindDialog(dialog) {
    if (dialog._bound) return;
    dialog._bound = true;
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) Drawer.close(dialog);
    });
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      Drawer.close(dialog);
    });
    dialog.addEventListener('close', function () {
      if (!qs('dialog[open]')) document.body.classList.remove('is-locked');
      var trigger = dialog._trigger;
      if (trigger) {
        if (trigger.hasAttribute && trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
        if (typeof trigger.focus === 'function' && document.contains(trigger)) trigger.focus({ preventScroll: true });
      }
    });
  }

  qsa('dialog[data-drawer], dialog[data-lightbox]').forEach(bindDialog);

  document.addEventListener('click', function (event) {
    var closeButton = event.target.closest('[data-drawer-close]');
    if (closeButton) {
      Drawer.close(closeButton.closest('dialog'));
      return;
    }

    var menuButton = event.target.closest('[data-menu-open]');
    if (menuButton) {
      Drawer.open(document.getElementById('MenuDrawer'), menuButton);
      return;
    }

    var cartLink = event.target.closest('[data-cart-open]');
    var cartDrawer = document.getElementById('CartDrawer');
    if (cartLink && cartDrawer && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      Drawer.open(cartDrawer, cartLink);
    }
  });

  /* ------------------------------------------------------------------------
     Cart (Shopify AJAX Cart API + Section Rendering API)
     ------------------------------------------------------------------------ */
  function CartError(message, data) {
    this.name = 'CartError';
    this.message = message || strings.errorGeneric;
    this.data = data || {};
  }
  CartError.prototype = Object.create(Error.prototype);

  function parseResponse(response) {
    return response
      .json()
      .catch(function () {
        return {};
      })
      .then(function (data) {
        if (!response.ok || data.status) {
          throw new CartError(data.description || data.message || strings.errorGeneric, data);
        }
        return data;
      });
  }

  function networkError() {
    throw new CartError(strings.errorNetwork || strings.errorGeneric);
  }

  var Cart = {
    sectionIds: function () {
      var ids = [];
      qsa('[data-cart-section]').forEach(function (element) {
        var id = element.getAttribute('data-cart-section');
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      return ids.slice(0, 5);
    },

    add: function (formData, withSections) {
      if (withSections) {
        formData.append('sections', Cart.sectionIds().join(','));
        formData.append('sections_url', window.location.pathname);
      }
      return fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      }).then(parseResponse, networkError);
    },

    change: function (key, quantity) {
      return fetch(routes.cartChange + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: Cart.sectionIds(),
          sections_url: window.location.pathname
        })
      }).then(parseResponse, networkError);
    },

    updateNote: function (note) {
      return fetch(routes.cartUpdate + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note })
      }).then(parseResponse, networkError);
    },

    refresh: function () {
      var ids = Cart.sectionIds();
      if (!ids.length) return Promise.resolve();
      var url = window.location.pathname + '?sections=' + encodeURIComponent(ids.join(','));
      return fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (sections) {
          Cart.render(sections);
        })
        .catch(function () {});
    },

    render: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (id) {
        var html = sections[id];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-section="' + cssEscape(id) + '"]');
        if (!fresh) return;
        qsa('[data-cart-section="' + cssEscape(id) + '"]').forEach(function (element) {
          element.innerHTML = fresh.innerHTML;
        });
      });
      document.dispatchEvent(new CustomEvent('nelvi:cart-updated'));
    }
  };

  window.NELVI.cart = Cart;

  var lineTimers = {};

  function showLineError(key, message) {
    var line = qs('[data-cart-line][data-line-key="' + cssEscape(key) + '"]');
    var target = line ? qs('[data-line-error]', line) : null;
    if (!target) {
      target = qs('[data-cart-error]');
    }
    if (target) {
      target.textContent = message;
      target.hidden = false;
    }
  }

  function changeLine(key, quantity, lineElement) {
    var root = lineElement ? lineElement.closest('[data-cart-section]') : null;
    if (lineElement) lineElement.classList.add('is-loading');
    if (root) root.setAttribute('aria-busy', 'true');

    return Cart.change(key, quantity)
      .then(function (data) {
        Cart.render(data.sections);
        announce(strings.cartUpdated);
      })
      .catch(function (error) {
        return Cart.refresh().then(function () {
          showLineError(key, error.message);
        });
      })
      .then(function () {
        qsa('[data-cart-section][aria-busy]').forEach(function (element) {
          element.removeAttribute('aria-busy');
        });
      });
  }

  function scheduleLineChange(input) {
    var key = input.getAttribute('data-line-key');
    window.clearTimeout(lineTimers[key]);
    lineTimers[key] = window.setTimeout(function () {
      var value = Math.max(0, parseInt(input.value, 10) || 0);
      changeLine(key, value, input.closest('[data-cart-line]'));
    }, 380);
  }

  document.addEventListener('click', function (event) {
    var stepButton = event.target.closest('[data-quantity-step]');
    if (stepButton) {
      var wrapper = stepButton.closest('[data-quantity]');
      var input = wrapper ? qs('input', wrapper) : null;
      if (!input) return;
      var min = input.min !== '' ? parseInt(input.min, 10) : 1;
      var max = input.max !== '' ? parseInt(input.max, 10) : Infinity;
      var current = parseInt(input.value, 10) || 0;
      var next = Math.max(min, Math.min(max, current + parseInt(stepButton.getAttribute('data-quantity-step'), 10)));
      if (next !== current) {
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    var removeLink = event.target.closest('[data-line-remove]');
    if (removeLink) {
      event.preventDefault();
      changeLine(removeLink.getAttribute('data-line-remove'), 0, removeLink.closest('[data-cart-line]'));
    }
  });

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (target.matches && target.matches('input[data-line-key]')) {
      scheduleLineChange(target);
      return;
    }
    if (target.matches && target.matches('[data-cart-note]')) {
      Cart.updateNote(target.value).catch(function () {});
      return;
    }
    var autoForm = target.closest && target.closest('form[data-auto-submit]');
    if (autoForm) autoForm.submit();
  });

  /* ------------------------------------------------------------------------
     Gallery
     ------------------------------------------------------------------------ */
  function Gallery(root) {
    this.root = root;
    this.track = qs('[data-gallery-track]', root);
    if (!this.track) return;
    this.slides = qsa('.gallery__slide', this.track);
    this.prevButton = qs('[data-gallery-prev]', root);
    this.nextButton = qs('[data-gallery-next]', root);
    this.lightbox = qs('[data-lightbox]', root);
    this.active = null;

    var self = this;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) self.setActive(entry.target);
          });
        },
        { root: this.track, threshold: [0.6] }
      );
      this.slides.forEach(function (slide) {
        self.observer.observe(slide);
      });
    }

    if (this.prevButton) {
      this.prevButton.addEventListener('click', function () {
        self.go(-1);
      });
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', function () {
        self.go(1);
      });
    }

    qsa('[data-thumb]', root).forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        self.scrollToMedia(thumb.getAttribute('data-thumb'), true);
      });
    });

    this.track.addEventListener('keydown', function (event) {
      var forwardKey = isRTL ? 'ArrowLeft' : 'ArrowRight';
      var backKey = isRTL ? 'ArrowRight' : 'ArrowLeft';
      if (event.key === forwardKey) {
        event.preventDefault();
        self.go(1);
      } else if (event.key === backKey) {
        event.preventDefault();
        self.go(-1);
      }
    });

    qsa('[data-zoom-open]', root).forEach(function (button) {
      button.addEventListener('click', function () {
        self.openZoom(button.getAttribute('data-zoom-open'), button);
      });
    });

    if (this.lightbox) {
      bindDialog(this.lightbox);
      this.lightbox.addEventListener('click', function (event) {
        var image = event.target.closest('.lightbox__image');
        if (image && window.matchMedia('(min-width: 990px)').matches) image.classList.toggle('is-zoomed');
      });
      qsa('[data-lightbox-close]', this.lightbox).forEach(function (button) {
        button.addEventListener('click', function () {
          Drawer.close(self.lightbox);
        });
      });
    }

    var start = root.getAttribute('data-start-media');
    var startSlide = this.find(start) || this.visible()[0];
    if (startSlide) {
      this.scrollToMedia(startSlide.getAttribute('data-media-id'), false);
    }
  }

  Gallery.prototype.find = function (mediaId) {
    return this.slides.filter(function (slide) {
      return slide.getAttribute('data-media-id') === String(mediaId);
    })[0];
  };

  Gallery.prototype.visible = function () {
    return this.slides.filter(function (slide) {
      return !slide.hidden;
    });
  };

  Gallery.prototype.setActive = function (slide) {
    if (!slide || slide.hidden) return;
    this.active = slide;
    var id = slide.getAttribute('data-media-id');
    qsa('[data-dot-for]', this.root).forEach(function (dot) {
      dot.classList.toggle('is-active', dot.getAttribute('data-dot-for') === id);
    });
    qsa('[data-thumb]', this.root).forEach(function (thumb) {
      if (thumb.getAttribute('data-thumb') === id) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    var visible = this.visible();
    var index = visible.indexOf(slide);
    if (this.prevButton) this.prevButton.disabled = index <= 0;
    if (this.nextButton) this.nextButton.disabled = index >= visible.length - 1;
  };

  Gallery.prototype.scrollToMedia = function (mediaId, smooth) {
    var slide = this.find(mediaId);
    if (!slide || slide.hidden) return;
    var behavior = smooth && !reducedMotion ? 'smooth' : 'instant';
    try {
      this.track.scrollTo({ left: slide.offsetLeft, behavior: behavior });
    } catch (error) {
      this.track.scrollLeft = slide.offsetLeft;
    }
    this.setActive(slide);
  };

  Gallery.prototype.go = function (delta) {
    var visible = this.visible();
    var index = visible.indexOf(this.active);
    var target = visible[Math.max(0, Math.min(visible.length - 1, index + delta))];
    if (target) this.scrollToMedia(target.getAttribute('data-media-id'), true);
  };

  Gallery.prototype.showVariant = function (mediaId) {
    if (!mediaId) return;
    var slide = this.find(mediaId);
    if (!slide) return;
    var changedGroup = false;
    if (this.root.getAttribute('data-grouping') === 'true') {
      var group = slide.getAttribute('data-media-group');
      var hiddenIds = {};
      this.slides.forEach(function (item) {
        var itemGroup = item.getAttribute('data-media-group');
        var hide = !(itemGroup === 'shared' || itemGroup === group);
        if (item.hidden !== hide) changedGroup = true;
        item.hidden = hide;
        if (hide) hiddenIds[item.getAttribute('data-media-id')] = true;
      });
      qsa('[data-thumb-for], [data-dot-for], [data-lightbox-item]', this.root).forEach(function (element) {
        var id =
          element.getAttribute('data-thumb-for') ||
          element.getAttribute('data-dot-for') ||
          element.getAttribute('data-lightbox-item');
        element.hidden = !!hiddenIds[id];
      });
    }
    this.root.classList.toggle('is-single', this.visible().length < 2);
    this.scrollToMedia(mediaId, !changedGroup);
  };

  Gallery.prototype.openZoom = function (mediaId, trigger) {
    if (!this.lightbox) return;
    Drawer.open(this.lightbox, trigger);
    var item = qs('[data-lightbox-item="' + cssEscape(mediaId) + '"]', this.lightbox);
    if (item) {
      var image = qs('img', item);
      if (image) image.loading = 'eager';
      window.requestAnimationFrame(function () {
        item.scrollIntoView({ block: 'start' });
      });
    }
  };

  /* ------------------------------------------------------------------------
     Product (main product + featured product)
     ------------------------------------------------------------------------ */
  class NelviProduct extends HTMLElement {}

  NelviProduct.prototype.connectedCallback = function () {
    if (this._ready) return;
    this._ready = true;
    var self = this;
    var json = qs('[data-product-json]', this);
    try {
      this.data = json ? JSON.parse(json.textContent) : { variants: [] };
    } catch (error) {
      this.data = { variants: [] };
    }
    this.form = document.getElementById(this.getAttribute('data-form-id'));
    this.picker = qs('[data-variant-picker]', this);
    this.addButton = this.form ? qs('[data-add-button]', this.form) : null;
    this.buyNowButton = this.form ? qs('[data-buy-now]', this.form) : null;
    this.errorBox = this.form ? qs('[data-form-error]', this.form) : null;
    this.sticky = qs('[data-sticky-cta]', this);

    var galleryRoot = qs('[data-gallery]', this);
    this.gallery = galleryRoot ? new Gallery(galleryRoot) : null;

    this.currentVariant = this.resolveVariant();
    this.updateOptionAvailability();

    if (this.picker) {
      this.picker.addEventListener('change', function () {
        self.onVariantChange();
      });
    }
    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        self.onSubmit(event);
      });
    }
    this.setupSticky();
    this.applyStoredTone();
  };

  /* The tone the visitor chose (gold / silver) follows them for the session: a product form that
     opens without a variant in the URL preselects the remembered silver variant. */
  NelviProduct.prototype.applyStoredTone = function () {
    if (!this.picker || !this.currentVariant) return;
    var fromUrl = new URL(window.location.href).searchParams.get('variant');
    if (fromUrl && this.getAttribute('data-update-url') === 'true') {
      setTone(toneOfVariant(this.currentVariant));
      return;
    }
    if (readTone() !== 'silver' || toneOfVariant(this.currentVariant) === 'silver') return;
    var target = qsa('input[data-option-value]', this.picker).filter(function (input) {
      return toneOfValue(input.getAttribute('data-option-value')) === 'silver' && !input.checked;
    })[0];
    if (!target) return;
    target.checked = true;
    this.onVariantChange();
  };

  NelviProduct.prototype.selectedOptions = function () {
    return qsa('[data-option-index]', this.picker).map(function (fieldset) {
      var checked = qs('input:checked', fieldset);
      return checked ? checked.getAttribute('data-option-value') : null;
    });
  };

  NelviProduct.prototype.resolveVariant = function () {
    var variants = this.data.variants || [];
    if (!variants.length) return null;
    if (!this.picker) {
      var idInput = this.form ? this.form.querySelector('[name="id"]') : null;
      var match = variants.filter(function (variant) {
        return idInput && String(variant.id) === idInput.value;
      })[0];
      return match || variants[0];
    }
    var selected = this.selectedOptions();
    return (
      variants.filter(function (variant) {
        return variant.options.every(function (option, index) {
          return option === selected[index];
        });
      })[0] || null
    );
  };

  NelviProduct.prototype.updateOptionAvailability = function () {
    if (!this.picker) return;
    var variants = this.data.variants || [];
    var selected = this.selectedOptions();
    qsa('[data-option-index]', this.picker).forEach(function (fieldset) {
      var optionIndex = parseInt(fieldset.getAttribute('data-option-index'), 10);
      qsa('input[data-option-value]', fieldset).forEach(function (input) {
        var value = input.getAttribute('data-option-value');
        var available = variants.some(function (variant) {
          if (!variant.available || variant.options[optionIndex] !== value) return false;
          return variant.options.every(function (option, index) {
            return index === optionIndex || selected[index] === null || option === selected[index];
          });
        });
        if (available) {
          input.removeAttribute('data-unavailable');
        } else {
          input.setAttribute('data-unavailable', '');
        }
        var label = input.nextElementSibling;
        var soldOut = label ? qs('[data-soldout-label]', label) : null;
        if (soldOut) soldOut.hidden = available;
      });
    });
  };

  NelviProduct.prototype.onVariantChange = function () {
    var variant = this.resolveVariant();
    this.currentVariant = variant;
    this.hideError();

    qsa('[data-option-index]', this.picker).forEach(function (fieldset) {
      var checked = qs('input:checked', fieldset);
      var current = qs('[data-option-current]', fieldset);
      if (checked && current) current.textContent = checked.getAttribute('data-option-value');
    });
    this.updateOptionAvailability();

    if (!variant) {
      this.setButtons(false, strings.unavailable);
      this.setAvailability('unavailable');
      return;
    }

    var hiddenId = this.form ? this.form.querySelector('input[type="hidden"][name="id"]') : null;
    if (hiddenId) hiddenId.value = variant.id;

    qsa('[data-price-container], [data-sticky-price]', this).forEach(function (element) {
      element.innerHTML = variant.price_html;
    });
    var stickyVariant = qs('[data-sticky-variant]', this);
    if (stickyVariant) stickyVariant.textContent = variant.title;

    this.setButtons(variant.available, variant.available ? strings.addToCart : strings.soldOut);
    this.setAvailability(variant.available ? 'available' : 'sold_out');

    var quantity = qs('input[name="quantity"]', this);
    if (quantity) {
      quantity.min = variant.quantity_min || 1;
      if (variant.quantity_max) {
        quantity.max = variant.quantity_max;
      } else {
        quantity.removeAttribute('max');
      }
    }

    if (this.gallery && variant.media_id) this.gallery.showVariant(variant.media_id);

    if (this.getAttribute('data-update-url') === 'true' && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    this.dispatchEvent(new CustomEvent('nelvi:variant-change', { bubbles: true, detail: { variant: variant } }));
  };

  NelviProduct.prototype.setButtons = function (enabled, label) {
    var buttons = [this.addButton, this.buyNowButton, qs('[data-sticky-add]', this)];
    buttons.forEach(function (button) {
      if (button) button.disabled = !enabled;
    });
    var addLabel = this.addButton ? qs('[data-add-label]', this.addButton) : null;
    if (addLabel) addLabel.textContent = label;
    var stickyLabel = qs('[data-sticky-label]', this);
    if (stickyLabel) stickyLabel.textContent = label;
  };

  NelviProduct.prototype.setAvailability = function (state) {
    var element = qs('[data-availability]', this);
    if (!element) return;
    var text = qs('[data-availability-text]', element);
    element.classList.toggle('availability--out', state !== 'available');
    if (text) {
      text.textContent =
        state === 'available'
          ? strings.availableNow
          : state === 'sold_out'
          ? strings.soldOutNotice
          : strings.unavailableNotice;
    }
  };

  NelviProduct.prototype.showError = function (message) {
    if (!this.errorBox) return;
    var text = qs('[data-form-error-text]', this.errorBox);
    if (text) text.textContent = message || strings.errorGeneric;
    this.errorBox.hidden = false;
  };

  NelviProduct.prototype.hideError = function () {
    if (this.errorBox) this.errorBox.hidden = true;
  };

  NelviProduct.prototype.onSubmit = function (event) {
    var submitter = event.submitter || null;
    var isBuyNow = !!(submitter && submitter.hasAttribute('data-buy-now'));
    var cartDrawer = document.getElementById('CartDrawer');
    var useAjax = isBuyNow || (NELVI.cartType === 'drawer' && cartDrawer);
    if (!useAjax) return; // native POST /cart/add -> Shopify shows the cart page

    event.preventDefault();
    if (!this.currentVariant || !this.currentVariant.available) return;

    var self = this;
    var button = submitter || this.addButton;
    this.hideError();
    [this.addButton, this.buyNowButton].forEach(function (item) {
      if (item) item.disabled = true;
    });
    if (button) {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    }

    var formData = new FormData(this.form);
    Cart.add(formData, !isBuyNow)
      .then(function (data) {
        if (isBuyNow) {
          // Standard cart -> Shopify Checkout path (keeps manual payment methods such as COD).
          var checkoutForm = qs('[data-checkout-form]');
          if (checkoutForm) {
            checkoutForm.submit();
          } else {
            window.location.href = routes.cart;
          }
          return;
        }
        Cart.render(data.sections);
        announce(strings.addedToCart);
        self.resetButtons(button);
        Drawer.open(cartDrawer, button);
      })
      .catch(function (error) {
        self.resetButtons(button);
        self.showError(error.message);
      });
  };

  NelviProduct.prototype.resetButtons = function (button) {
    var available = this.currentVariant && this.currentVariant.available;
    [this.addButton, this.buyNowButton].forEach(function (item) {
      if (item) item.disabled = !available;
    });
    if (button) {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  };

  NelviProduct.prototype.setupSticky = function () {
    var sticky = this.sticky;
    var anchor = qs('[data-main-buy-buttons]', this);
    if (!sticky || !anchor || !('IntersectionObserver' in window)) return;
    var self = this;
    var buttonsAbove = false;
    var sectionInView = true;
    var update = function () {
      var show = buttonsAbove && sectionInView;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.body.classList.toggle('has-sticky-cta', show && window.innerWidth < 990);
    };
    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        buttonsAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      });
      update();
    }).observe(anchor);
    /* The bar stays while the visitor reads the rest of the page, and leaves before the footer:
       it hides as soon as the footer (or the section's end marker) rises above the bar's top edge,
       so the last rows of the page are never covered. */
    var endMarker = qs('.footer') || qs('[data-sticky-end]', this);
    if (endMarker) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            sectionInView = !entry.isIntersecting && entry.boundingClientRect.top > 0;
          });
          update();
        },
        { rootMargin: '0px 0px -' + (sticky.offsetHeight || 72) + 'px 0px' }
      ).observe(endMarker);
    }
    var stickyButton = qs('[data-sticky-add]', sticky);
    if (stickyButton) {
      stickyButton.addEventListener('click', function () {
        if (!self.form || !self.addButton) return;
        if (typeof self.form.requestSubmit === 'function') {
          self.form.requestSubmit(self.addButton);
        } else {
          self.addButton.click();
        }
      });
    }
  };

  /* ------------------------------------------------------------------------
     Gold / silver tone: html[data-tone] swaps the hero photo (CSS) and is remembered per session
     ------------------------------------------------------------------------ */
  var TONE_KEY = 'nelvi:tone';
  var silverValues = String(NELVI.silverValues || 'silver')
    .split(',')
    .map(function (value) {
      return value.trim().toLowerCase();
    })
    .filter(Boolean);

  function toneOfValue(value) {
    return silverValues.indexOf(String(value || '').trim().toLowerCase()) !== -1 ? 'silver' : 'gold';
  }

  function toneOfVariant(variant) {
    var silver = (variant.options || []).some(function (value) {
      return toneOfValue(value) === 'silver';
    });
    return silver ? 'silver' : 'gold';
  }

  function readTone() {
    try {
      return sessionStorage.getItem(TONE_KEY);
    } catch (error) {
      return null;
    }
  }

  function setTone(tone) {
    if (tone === 'silver') {
      document.documentElement.setAttribute('data-tone', 'silver');
    } else {
      document.documentElement.removeAttribute('data-tone');
    }
    try {
      sessionStorage.setItem(TONE_KEY, tone);
    } catch (error) {}
  }

  document.addEventListener('nelvi:variant-change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (variant) setTone(toneOfVariant(variant));
  });

  /* ------------------------------------------------------------------------
     Campaign films: progressive enhancement over the hero still.
     Loads only near the viewport, never with reduced motion or Save-Data, fades in once playing,
     and follows the gold / silver tone.
     ------------------------------------------------------------------------ */
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var films = qsa('[data-hero-film]');

  function filmSource(video) {
    var silver = document.documentElement.getAttribute('data-tone') === 'silver';
    var mobile = window.innerWidth < 750;
    if (silver && video.getAttribute('data-src-silver')) {
      return (mobile && video.getAttribute('data-src-silver-mobile')) || video.getAttribute('data-src-silver');
    }
    return (mobile && video.getAttribute('data-src-mobile')) || video.getAttribute('data-src');
  }

  function loadFilm(video) {
    var src = filmSource(video);
    if (!src || video.getAttribute('src') === src) return;
    video.classList.remove('is-playing');
    video.setAttribute('src', src);
    video.load();
    var promise = video.play();
    if (promise && promise.catch) promise.catch(function () {});
  }

  if (films.length && !reducedMotion && !saveData) {
    films.forEach(function (video) {
      if (video.hasAttribute('data-film-desktop-only') && window.innerWidth < 750) return;
      video.addEventListener('playing', function () {
        video.classList.add('is-playing');
      });
      var start = function () {
        loadFilm(video);
      };
      if ('IntersectionObserver' in window) {
        var filmObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              filmObserver.disconnect();
              start();
            });
          },
          { rootMargin: '240px 0px' }
        );
        filmObserver.observe(video);
      } else {
        start();
      }
    });
    document.addEventListener('nelvi:variant-change', function () {
      films.forEach(function (video) {
        if (video.getAttribute('src')) loadFilm(video);
      });
    });
  }

  if (window.customElements && !customElements.get('nelvi-product')) {
    customElements.define('nelvi-product', NelviProduct);
  }

  /* ------------------------------------------------------------------------
     Reveal on scroll (disabled in the theme editor and for reduced motion)
     ------------------------------------------------------------------------ */
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  if (document.body.classList.contains('has-reveal') && !reducedMotion && !designMode && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('reveal-ready');
    var revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
    );
    qsa('[data-reveal]').forEach(function (element) {
      revealObserver.observe(element);
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    qsa('[data-reveal]', event.target).forEach(function (element) {
      element.classList.add('is-revealed');
    });
    qsa('dialog[data-drawer], dialog[data-lightbox]', event.target).forEach(bindDialog);
  });

  /* The header has no rule under it; once the page scrolls it takes a tone step instead. */
  var header = qs('[data-header]');
  if (header) {
    var stuck = null;
    var toggleStuck = function () {
      var next = window.scrollY > 12;
      if (next === stuck) return;
      stuck = next;
      header.classList.toggle('is-stuck', next);
    };
    toggleStuck();
    window.addEventListener('scroll', toggleStuck, { passive: true });
  }

  /* Floating WhatsApp button appears after the first scroll, so it never covers the hero CTA. */
  var whatsappFloat = qs('[data-whatsapp-float]');
  if (whatsappFloat) {
    var toggleFloat = function () {
      whatsappFloat.classList.toggle('is-shown', window.scrollY > Math.min(420, window.innerHeight * 0.5));
    };
    toggleFloat();
    window.addEventListener('scroll', toggleFloat, { passive: true });
  }

  /* Restore buttons when returning with the browser's back button (bfcache). */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    qsa('.btn.is-loading').forEach(function (button) {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    });
    qsa('nelvi-product').forEach(function (product) {
      if (product.resetButtons) product.resetButtons();
    });
    Cart.refresh();
  });
})();
